import type { ElementType, MouseEventHandler, ReactEventHandler, ReactNode } from 'react';
import type { To } from 'react-router';
import { merge } from 'lodash-es';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { TabProps } from '../../controls/Tabs/Tabs';
import type { ShellPropLayoutMode, ShellPropSize, ShellPropType } from './utils';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import { Icon } from '../../assets/Icon/Icon';
import { atContainers, containers } from '../../common/containers';
import { devices } from '../../common/devices';
import { selectors } from '../../common/selectors';
import { sizing } from '../../common/sizing';
import { Badge } from '../../components/Badge/Badge';
import { BadgeGroup } from '../../components/Badge/BadgeGroup';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { Tabs } from '../../controls/Tabs/Tabs';
import { UtilityControl } from '../../controls/UtilityControl/UtilityControl';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import {
  shellControlGroupSize,
  shellVars,
  useShellMaxWidth,
  useShellMinWidth,
  useShellSize,
  useShellType,
  useShellWidth,
  useShellWidthMode,
} from './utils';

export type ShellHeaderProps = {
  /**
   * Provide any actions for your pane.
   */
  actions?: ReactNode;
  /**
   * Show relevant badges alongside the `heading`.
   */
  badges?: ReactNode;
  /**
   * Provide a label for the close button.
   */
  closeButtonAriaLabel?: string;
  /**
   * Show the total count of items.
   */
  count?: ReactNode;
  /**
   * Provide a heading for your pane.
   */
  heading?: ReactNode;
  /**
   * Provide an icon for your pane.
   */
  icon?: DynamicIconName;
  /**
   * Provide a handler and opt into adding a close icon to the right.
   */
  onClose?: ReactEventHandler;
  /**
   * Provide a parent for your pane.
   */
  parent?: {
    /**
     * Provide an icon for the parent.
     */
    icon?: DynamicIconName;
    /**
     * Provide a label for the parent.
     */
    label?: ReactNode;
    // Routing
    as?: ElementType;
    to?: To;
  };
  /**
   * Provide any subtabs in addition to tabs.
   */
  secondaryTabs?: TabProps[];
  /**
   * Allows for a select interface to switch between objects.
   */
  switcher?: ReactNode;
  /**
   * Provide any tabs you want to display.
   */
  tabs?: TabProps[];
  /**
   * If only one tab is visible, don't display it.
   */
  hideSingleTab?: boolean;
  /**
   * Provide the views and filters utilizing the `views` prop.
   */
  views?: ReactNode;
  /**
   * Provide a layout mode for your shell.
   * Note: Automatically provided from the `Shell` component, you likely don't need to provide this.
   */
  layoutMode?: ShellPropLayoutMode;
  /**
   * Provide a type for your shell.
   * Note: Automatically provided from the `Shell` component, you likely don't need to provide this.
   */
  type?: ShellPropType;
  /**
   * Provide a size for your shell.
   * Note: Automatically provided from the `Shell` component, you likely don't need to provide this.
   */
  size?: ShellPropSize;
};

const shellHeaderValues = {
  ends: {
    'x-small': {
      controls: '$4',
      copy: '$6',
      tabs: '$4',
    },
    small: {
      controls: '$6',
      copy: '$8',
      tabs: '$6',
    },
    medium: {
      controls: '$8',
      copy: '$10',
      tabs: '$8',
    },
  },
  sides: {
    'x-small': {
      edges: `${sizing.contentSides}`,
      between: '$4',
    },
    small: {
      edges: `${sizing.contentSides}`,
      between: '$4',
    },
    medium: {
      edges: `${sizing.sides}`,
      between: '$6',
    },
  },
};

function shellHeaderBlock(size: ShellPropSize, content: 'controls' | 'copy' | 'tabs') {
  return {
    padding: `${shellHeaderValues.ends[size][content]} ${shellHeaderValues.sides[size].between}`,
  };
}

const shellHeaderDivider = (end: 'top' | 'bottom') => ({
  content: '',
  position: 'absolute',
  top: end === 'top' ? '-0.5px' : undefined,
  bottom: end === 'bottom' ? '-0.5px' : undefined,
  display: 'flex',
  height: '$1',
  borderRadius: '99em',
  backgroundColor: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.strokeNeutralDark,
  },
});

function shellHeaderDividerAtSize(size: ShellPropSize, end: 'top' | 'bottom') {
  const pseudo = end === 'top' ? 'before' : 'after';
  return {
    [`&::${pseudo}`]: {
      ...shellHeaderDivider(end),
      right: shellHeaderValues.sides[size].edges,
      left: shellHeaderValues.sides[size].edges,
    },
  };
}

const SHELL_HEADER_SIZES = ['x-small', 'small', 'medium'] satisfies readonly ShellPropSize[];

/** Narrow pane / drawer / dialog: shared container preludes for shell header padding rules. */
const SHELL_HEADER_NARROW_FRAME = [
  containers.pane.narrow,
  containers.drawer.all,
  containers.dialog.all,
];

function shellHeaderSizeVariants<T>(build: (size: ShellPropSize) => T): Record<ShellPropSize, T> {
  return Object.fromEntries(SHELL_HEADER_SIZES.map((size) => [size, build(size)])) as Record<
    ShellPropSize,
    T
  >;
}

type ShellLockupSidecarProps = {
  badges?: ReactNode;
  count?: ReactNode;
  size: ShellPropSize;
};

function ShellLockupSidecar({ badges, count, size }: ShellLockupSidecarProps) {
  const hasBadges = Boolean(badges || count);
  if (!hasBadges) return null;

  return (
    <BadgeGroup relation="separate" size={size === 'x-small' ? 'x-small' : 'small'}>
      {badges}
      {count && (
        <Badge ends="card" size={size === 'x-small' ? 'x-small' : 'small'} variant="neutral">
          {count}
        </Badge>
      )}
    </BadgeGroup>
  );
}

export type ShellHeaderLockupProps = {
  chevron?: boolean;
  icon?: DynamicIconName;
  isParent?: boolean;
  label: ReactNode;
  // Routing
  as?: ElementType;
  to?: To;
  onClick?: MouseEventHandler;
} & ShellLockupSidecarProps;

const SHELL_HEADER_LOCKUP_ICON_SIZE: Record<ShellPropSize, string> = {
  'x-small': '$12',
  small: '$14',
  medium: '$16',
};

const ShellHeaderLockupIcon = styled(DynamicIcon, {
  variants: {
    hasSize: shellHeaderSizeVariants((size) => ({
      size: SHELL_HEADER_LOCKUP_ICON_SIZE[size],
    })),
  },
});
ShellHeaderLockupIcon.displayName = 'ShellHeaderLockupIcon';

const ShellHeaderLockupLabel = styled(Text, {
  maxWidth: '$240',

  variants: {
    isParent: {
      true: {
        ...atContainers(SHELL_HEADER_NARROW_FRAME, {
          display: 'none',
        }),
      },
      false: {},
    },
  },
});

const ShellHeaderLockupContainer = styled('div', {
  position: 'relative',
  minWidth: 0,

  [selectors.focus]: {
    outline: 'none',

    '&::before': {
      content: '',
      position: 'absolute',
      inset: '-$4 -$6',
      display: 'flex',
      borderRadius: '$8',
      boxShadow: shadows.focusRingLight,

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },

  variants: {
    isParent: {
      true: {
        [devices.touch]: { display: 'none' },
        ...atContainers([containers.drawer.all, containers.dialog.all], { display: 'none' }),

        [containers.pane.narrow]: {
          widthAll: 'min-content',
        },
      },
    },
  },
});
ShellHeaderLockupContainer.displayName = 'ShellHeaderLockupContainer';

function ShellHeaderLockup({
  badges,
  count,
  chevron,
  icon,
  isParent,
  label,
  size,
  // Routing
  as,
  to,
  onClick,
  ...remaining
}: ShellHeaderLockupProps) {
  const isPressable = Boolean(as === 'button' || to || onClick);
  const hasBadges = Boolean(badges || count);
  const hasEnd = Boolean(chevron || hasBadges);

  return (
    <ShellHeaderLockupContainer
      tabIndex={isPressable ? 0 : undefined}
      role={isPressable ? 'button' : undefined}
      as={as}
      to={to}
      onClick={onClick}
      isParent={isParent}
      {...remaining}
    >
      <AlignStack
        align="center"
        direction="row"
        gap={{
          start: size === 'medium' ? 6 : 4,
          end: size === 'medium' ? 6 : 4,
        }}
        preset={size === 'medium' ? 'body' : 'small'}
        start={icon && <ShellHeaderLockupIcon icon={icon} variant="neutral" hasSize={size} />}
        end={
          hasEnd && (
            <>
              {chevron && <Icon icon="chevron-right" size={10} variant="neutral" />}
              <ShellLockupSidecar badges={badges} count={count} size={size} />
            </>
          )
        }
      >
        {label && (
          <ShellHeaderLockupLabel
            isParent={isParent}
            context="heading"
            variant="neutral"
            weight="bold"
            truncate
          >
            {label}
          </ShellHeaderLockupLabel>
        )}
      </AlignStack>
    </ShellHeaderLockupContainer>
  );
}

const ShellSwitcherContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',

  variants: {
    size: shellHeaderSizeVariants((size) => ({
      heightAll: size === 'x-small' ? '$16' : '$20',
    })),
  },
});
ShellSwitcherContainer.displayName = 'ShellSwitcherContainer';

type ShellSwitcherProps = {
  switcher: ReactNode;
} & ShellLockupSidecarProps;

function ShellSwitcher({ badges, count, size, switcher }: ShellSwitcherProps) {
  return (
    <ShellSwitcherContainer size={size}>
      {switcher}
      <ShellLockupSidecar badges={badges} count={count} size={size} />
    </ShellSwitcherContainer>
  );
}

const ShellHeaderLockups = styled('div', {
  gridArea: 'lockups',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  maxWidth: 'fit-content',
  minWidth: 0,
  gap: '$8',

  variants: {
    hasTabs: {
      true: {},
      false: {},
    },
    hasActions: {
      true: {},
      false: {},
    },
    hasClose: {
      true: {},
      false: {},
    },
    size: shellHeaderSizeVariants((size) => ({
      ...shellHeaderBlock(size, 'copy'),
      paddingLeft: shellHeaderValues.sides[size].edges,
    })),
  },

  compoundVariants: [
    ...SHELL_HEADER_SIZES.map((size) => ({
      hasClose: false,
      size,
      css: {
        ...atContainers(SHELL_HEADER_NARROW_FRAME, {
          paddingRight: shellHeaderValues.sides[size].edges,
        }),
      },
    })),
    ...SHELL_HEADER_SIZES.map((size) => ({
      hasTabs: false,
      hasActions: false,
      hasClose: false,
      size,
      css: {
        paddingRight: shellHeaderValues.sides[size].edges,
      },
    })),
  ],
});
ShellHeaderLockups.displayName = 'ShellHeaderLockups';

const SHELL_HEADER_TABS_FADE_FALLBACK = shellHeaderValues.sides['x-small'].between;
const SHELL_HEADER_TABS_FADE_SIZE_MULTIPLIER = 1;

function shellHeaderTabsFadeSizeCSS(token: string): string {
  const keyStr = token.startsWith('$') ? token.slice(1) : token;
  const px = Number(keyStr);
  if (!Number.isFinite(px) || px < 0) {
    return `var(--space-${keyStr})`;
  }
  const rounded = Math.ceil(px * SHELL_HEADER_TABS_FADE_SIZE_MULTIPLIER);
  return `var(--space-${rounded})`;
}

const shellHeaderTabsFadeSize = shellHeaderTabsFadeSizeCSS(SHELL_HEADER_TABS_FADE_FALLBACK);

const ShellHeaderTabs = styled('div', {
  gridArea: 'tabs',
  overflow: 'auto hidden',
  maskImage: `linear-gradient(to right, transparent, black ${shellHeaderTabsFadeSize}, black calc(100% - ${shellHeaderTabsFadeSize}), transparent)`,

  variants: {
    hasLockups: {
      true: {},
      false: {},
    },
    hasActions: {
      true: {},
      false: {},
    },
    hasClose: {
      true: {},
      false: {},
    },
    size: shellHeaderSizeVariants((size) => ({
      ...shellHeaderBlock(size, 'tabs'),
      ...atContainers(SHELL_HEADER_NARROW_FRAME, {
        paddingRight: shellHeaderValues.sides[size].edges,
        paddingLeft: shellHeaderValues.sides[size].edges,
      }),
    })),
  },

  compoundVariants: [
    ...SHELL_HEADER_SIZES.map((size) => ({
      hasLockups: false,
      size,
      css: {
        [containers.pane.notNarrow]: {
          paddingLeft: shellHeaderValues.sides[size].edges,
        },
      },
    })),
    ...SHELL_HEADER_SIZES.map((size) => ({
      hasActions: false,
      hasClose: false,
      size,
      css: {
        [containers.pane.notNarrow]: {
          paddingRight: shellHeaderValues.sides[size].edges,
        },
      },
    })),
  ],
});
ShellHeaderTabs.displayName = 'ShellHeaderTabs';

const ShellHeaderActionsContainer = styled('div', {
  gridArea: 'actions',

  variants: {
    hasLockups: {
      true: {},
      false: {},
    },
    hasTabs: {
      true: {},
      false: {},
    },
    hasClose: {
      true: {},
      false: {},
    },
    size: shellHeaderSizeVariants((size) => ({
      ...shellHeaderBlock(size, 'controls'),
    })),
  },

  compoundVariants: [
    ...SHELL_HEADER_SIZES.map((size) => ({
      hasTabs: false,
      size,
      css: {
        ...atContainers(SHELL_HEADER_NARROW_FRAME, {
          paddingLeft: shellHeaderValues.sides[size].edges,
        }),
      },
    })),
    ...SHELL_HEADER_SIZES.map((size) => ({
      hasLockups: false,
      hasTabs: false,
      size,
      css: {
        ...atContainers([containers.pane.notNarrow], {
          paddingLeft: shellHeaderValues.sides[size].edges,
        }),
      },
    })),
    ...SHELL_HEADER_SIZES.map((size) => ({
      hasClose: false,
      size,
      css: {
        paddingRight: shellHeaderValues.sides[size].edges,
      },
    })),
  ],
});
ShellHeaderActionsContainer.displayName = 'ShellHeaderActionsContainer';

function ShellHeaderActions({
  actions,
  hasLockups,
  hasTabs,
  hasClose,
  size,
}: {
  actions: ShellHeaderProps['actions'];
  hasLockups: boolean;
  hasTabs: boolean;
  hasClose: boolean;
  size: ShellPropSize;
}) {
  const effectiveSize = shellControlGroupSize(size);

  if (!actions) return null;

  return (
    <ShellHeaderActionsContainer
      hasLockups={hasLockups}
      hasTabs={hasTabs}
      hasClose={hasClose}
      size={size}
    >
      <ControlGroup size={effectiveSize} relation="separate">
        {actions}
      </ControlGroup>
    </ShellHeaderActionsContainer>
  );
}

const ShellHeaderCloseContainer = styled('div', {
  gridArea: 'close',

  variants: {
    hasLockups: {
      false: {},
      true: {},
    },
    hasTabs: {
      false: {},
      true: {},
    },
    hasActions: {
      false: {},
      true: {},
    },
    size: shellHeaderSizeVariants((size) => ({
      ...shellHeaderBlock(size, 'controls'),
      paddingRight: shellHeaderValues.sides[size].edges,
    })),
  },

  compoundVariants: [
    ...SHELL_HEADER_SIZES.map((size) => ({
      hasLockups: false,
      hasTabs: false,
      hasActions: false,
      size,
      css: {
        paddingLeft: shellHeaderValues.sides[size].edges,
      },
    })),
  ],
});
ShellHeaderCloseContainer.displayName = 'ShellHeaderCloseContainer';

function ShellHeaderClose({
  hasLockups,
  hasTabs,
  hasActions,
  onClose,
  closeButtonAriaLabel,
  size,
}: {
  hasLockups: boolean;
  hasTabs: boolean;
  hasActions: boolean;
  onClose: ReactEventHandler;
  closeButtonAriaLabel: string;
  size: ShellPropSize;
}) {
  return (
    <ShellHeaderCloseContainer
      hasLockups={hasLockups}
      hasTabs={hasTabs}
      hasActions={hasActions}
      size={size}
    >
      <UtilityControl
        onClick={onClose}
        label={closeButtonAriaLabel}
        variant="neutral"
        shortcut={['ESC']}
      />
    </ShellHeaderCloseContainer>
  );
}

const ShellHeaderSlot = styled('div', {
  position: 'relative',

  variants: {
    name: {
      top: {
        [containers.pane.notNarrow]: {
          display: 'contents',
        },

        ...atContainers(SHELL_HEADER_NARROW_FRAME, {
          gridArea: 'top',
          display: 'grid',
          alignItems: 'center',
          gridTemplateAreas: '"lockups actions close"',
          gridTemplateColumns: '1fr min-content min-content',
          gridTemplateRows: 'min-content',
        }),
      },

      middle: {
        [containers.pane.notNarrow]: {
          display: 'contents',
        },

        ...atContainers(SHELL_HEADER_NARROW_FRAME, {
          gridArea: 'middle',
          display: 'grid',
          alignItems: 'center',
          gridTemplateAreas: '"tabs"',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'min-content',
        }),
      },
    },
    size: shellHeaderSizeVariants((size) =>
      atContainers(SHELL_HEADER_NARROW_FRAME, {
        ...shellHeaderDividerAtSize(size, 'bottom'),
      }),
    ),
  },
});
ShellHeaderSlot.displayName = 'ShellHeaderSlot';

const ShellHeaderViews = styled('div', {
  gridArea: 'bottom',
  position: 'relative',

  variants: {
    size: shellHeaderSizeVariants((size) => shellHeaderDividerAtSize(size, 'top')),
  },
});
ShellHeaderViews.displayName = 'ShellHeaderViews';

const ShellHeaderGrid = styled('div', {
  position: 'relative',
  display: 'grid',
  alignItems: 'center',

  ...atContainers(SHELL_HEADER_NARROW_FRAME, {
    gridTemplateAreas: '"top" "middle" "bottom"',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'min-content min-content min-content',
  }),

  [containers.pane.intermediate]: {
    gridTemplateAreas: '"lockups tabs actions close" "bottom bottom bottom bottom"',
    gridTemplateColumns: 'min-content 1fr min-content min-content',
    gridTemplateRows: 'min-content min-content',
  },

  [containers.pane.wide]: {
    gridTemplateAreas: '"lockups tabs actions close" "bottom bottom bottom bottom"',
    gridTemplateColumns: 'min-content 1fr min-content',
    gridTemplateRows: 'min-content min-content',
  },

  variants: {
    size: shellHeaderSizeVariants((size) => shellHeaderDividerAtSize(size, 'bottom')),
  },
});
ShellHeaderGrid.displayName = 'ShellHeaderGrid';

const ShellHeaderPosition = styled('div', {
  containerType: 'inline-size',
  minWidth: 0,

  variants: {
    size: {
      'x-small': {
        [devices.touch]: {
          minHeight: shellVars.height['x-small'].touch,
        },

        [devices.mouse]: {
          minHeight: shellVars.height['x-small'].mouse,
        },
      },
      small: {
        [devices.touch]: {
          minHeight: shellVars.height.small.touch,
        },

        [devices.mouse]: {
          minHeight: shellVars.height.small.mouse,
        },
      },
      medium: {
        [devices.touch]: {
          minHeight: shellVars.height.medium.touch,
        },

        [devices.mouse]: {
          minHeight: shellVars.height.medium.mouse,
        },
      },
    },
  },
});
ShellHeaderPosition.displayName = 'ShellHeaderPosition';

const ShellHeaderContainer = styled('div', {
  gridArea: 'shell-header',
  position: 'relative',
  zIndex: 2,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',

  variants: {
    widthMode: {
      centered: {
        alignItems: 'center',
      },
      full: {},
    },
  },
});
ShellHeaderContainer.displayName = 'ShellHeaderContainer';

export function ShellHeader({
  actions,
  badges,
  closeButtonAriaLabel = 'Close',
  count,
  heading,
  hideSingleTab = true,
  icon,
  layoutMode,
  onClose,
  parent,
  size,
  secondaryTabs,
  switcher,
  tabs,
  type: typeProp,
  views,
  ...remaining
}: ShellHeaderProps) {
  const shellType = useShellType(typeProp);
  const effectiveSize = useShellSize(size, 'medium');
  const effectiveWidth = useShellWidth();
  const effectiveMaxWidth = useShellMaxWidth();
  const effectiveMinWidth = useShellMinWidth();
  const effectiveWidthMode = useShellWidthMode();

  const hasHeading = Boolean(icon || switcher || heading || badges || count);
  const hasLockups = Boolean(parent || hasHeading);

  const hasSecondaryTabs = Boolean(
    secondaryTabs && (secondaryTabs.length > 1 || (secondaryTabs.length > 0 && !hideSingleTab)),
  );
  const hasPrimaryTabs = Boolean(tabs && (tabs.length > 1 || (tabs.length > 0 && !hideSingleTab)));
  const hasTabs = hasPrimaryTabs || hasSecondaryTabs;
  const effectiveTabsSize = shellControlGroupSize(effectiveSize);

  const hasActions = Boolean(actions);
  const hasClose = Boolean(onClose);
  const hasViews = Boolean(views);

  const hasTop = hasLockups || hasClose || hasActions;
  const hasMiddle = hasTabs || hasActions;
  const hasBottom = hasViews;

  const hasHeader = hasTop || hasMiddle || hasBottom;

  if (!hasHeader) return null;

  return (
    <ShellHeaderContainer widthMode={effectiveWidthMode} {...remaining}>
      <ShellHeaderPosition
        css={merge(
          effectiveWidth ? widthCSS(effectiveWidth) : undefined,
          effectiveMaxWidth ? maxWidthCSS(effectiveMaxWidth) : undefined,
          effectiveMinWidth ? minWidthCSS(effectiveMinWidth) : undefined,
          { containerName: shellType },
        )}
        size={effectiveSize}
      >
        <ShellHeaderGrid size={effectiveSize}>
          {hasTop && (
            <ShellHeaderSlot name="top" size={effectiveSize}>
              {hasLockups && (
                <ShellHeaderLockups
                  hasTabs={hasTabs}
                  hasActions={hasActions}
                  hasClose={hasClose}
                  size={effectiveSize}
                >
                  {parent && (
                    <ShellHeaderLockup
                      isParent
                      as={parent.as}
                      to={parent.to}
                      chevron
                      icon={parent.icon}
                      label={parent.label}
                      size={effectiveSize}
                    />
                  )}
                  {hasHeading && (
                    <>
                      {switcher && (
                        <ShellSwitcher
                          badges={badges}
                          count={count}
                          size={effectiveSize}
                          switcher={switcher}
                        />
                      )}
                      {!switcher && (
                        <ShellHeaderLockup
                          icon={icon}
                          label={heading}
                          size={effectiveSize}
                          badges={badges}
                          count={count}
                        />
                      )}
                    </>
                  )}
                </ShellHeaderLockups>
              )}
              {hasActions && (
                <ShellHeaderActions
                  hasLockups={hasLockups}
                  hasTabs={hasTabs}
                  hasClose={hasClose}
                  actions={actions}
                  size={effectiveSize}
                />
              )}
              {onClose && (
                <ShellHeaderClose
                  hasLockups={hasLockups}
                  hasTabs={hasTabs}
                  hasActions={hasActions}
                  onClose={onClose}
                  closeButtonAriaLabel={closeButtonAriaLabel}
                  size={effectiveSize}
                />
              )}
            </ShellHeaderSlot>
          )}
          {hasMiddle && (
            <ShellHeaderSlot name="middle" size={effectiveSize}>
              {hasTabs && (
                <ShellHeaderTabs
                  hasLockups={hasLockups}
                  hasActions={hasActions}
                  hasClose={hasClose}
                  size={effectiveSize}
                >
                  <Tabs
                    tabs={tabs}
                    secondaryTabs={secondaryTabs}
                    size={effectiveTabsSize}
                    hideSingleTab={hideSingleTab}
                  />
                </ShellHeaderTabs>
              )}
            </ShellHeaderSlot>
          )}
          {hasViews && <ShellHeaderViews size={effectiveSize}>{views}</ShellHeaderViews>}
        </ShellHeaderGrid>
      </ShellHeaderPosition>
    </ShellHeaderContainer>
  );
}
